/**
 * DraftSheet — the resizable persistent side panel that replaces the
 * original Drawer-based DraftPanel.
 *
 * Per the UX/UI report (§5/W3): the original Drawer was modal-feeling,
 * hid the chat behind it, and easy to lose. The Sheet is a positioned
 * Box (not a Drawer): it composes inline with the layout, persists
 * across navigation, and offers a collapse-to-icon affordance instead
 * of a hard close.
 *
 * Three modes (driven by `draftStore.panelMode`):
 *   * `closed`     — fully out of view; the only entry is the rack
 *                    DraftSegment / SnapshotPill click.
 *   * `collapsed`  — 44px icon column on the right edge with a single
 *                    chevron. Click to re-expand.
 *   * `open`       — full sheet at the user's chosen width
 *                    (persisted in `panelWidth`).
 *
 * Tabs (top of the sheet):
 *   * Findings — DRC findings (the existing DrcFindingsDrawer)
 *   * Lineage  — the new OpTimelineStrip + the existing SnapshotChainView
 *   * Inspect  — IR overview (component + net counts, source format,
 *                "Open in canvas" gesture once Phase 8 lands)
 *   * BOM      — quick BOM access (download + merge)
 */
import { BlueprintEmptyState, PenMotif } from "@components/EmptyState";
import { exportSnapshotBomCsvUrl, useSnapshot } from "@features/circuits/hooks/useCircuitIr";
import { PromoteScratchDialog } from "@features/projects/components/PromoteScratchDialog";
import { BomMergeDialog } from "@features/upload/components/BomMergeDialog";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeftRounded";
import ChevronRightIcon from "@mui/icons-material/ChevronRightRounded";
import CloseIcon from "@mui/icons-material/CloseRounded";
import CommitIcon from "@mui/icons-material/CloudUploadRounded";
import DownloadIcon from "@mui/icons-material/DownloadRounded";
import BomIcon from "@mui/icons-material/Inventory2Rounded";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  Stack,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from "@mui/material";
import { useDraftStore } from "@stores/draftStore";
import { useProjectStore } from "@stores/projectStore";
import { useState } from "react";

import { CommitDraftDialog } from "./CommitDraftDialog";
import { DraftResizeHandle } from "./DraftResizeHandle";
import { DrcFindingsDrawer } from "./DrcFindingsDrawer";
import { OpTimelineStrip } from "./OpTimelineStrip";

interface DraftSheetProps {
  projectId: string | null;
  parentVersionId?: string | null;
}

const COLLAPSED_WIDTH = 44;

export function DraftSheet({ projectId, parentVersionId }: DraftSheetProps) {
  const {
    panelMode,
    panelWidth,
    panelTab,
    activeSnapshotId,
    setPanelTab,
    setActiveSnapshot,
    openPanel,
    collapsePanel,
    closePanel,
  } = useDraftStore();
  // Active Circuit (Phase 14 multi-Circuit projects) — threaded into
  // CommitDraftDialog so the commit lands on the right circuit. Falls
  // through to undefined for single-circuit legacy projects, where the
  // backend defaults to `main`.
  const activeCircuitId = useProjectStore((s) => s.activeCircuitId);
  const { data: snapshot, isLoading } = useSnapshot(activeSnapshotId);
  const [commitOpen, setCommitOpen] = useState(false);
  const [bomOpen, setBomOpen] = useState(false);
  const [promoteOpen, setPromoteOpen] = useState(false);

  if (panelMode === "closed") {
    return (
      <>
        <CommitDraftDialog
          open={commitOpen}
          snapshotId={activeSnapshotId}
          projectId={projectId}
          circuitId={activeCircuitId}
          parentVersionId={parentVersionId}
          onClose={() => setCommitOpen(false)}
        />
        <BomMergeDialog
          open={bomOpen}
          snapshotId={activeSnapshotId}
          onClose={() => setBomOpen(false)}
          onMerged={(report) => setActiveSnapshot(report.snapshot_id)}
        />
      </>
    );
  }

  if (panelMode === "collapsed") {
    return (
      <Box
        sx={(t) => ({
          width: COLLAPSED_WIDTH,
          flex: "0 0 auto",
          borderLeft: `1px solid ${t.palette.border.default}`,
          bgcolor: t.palette.surface.default,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          py: 1.25,
          gap: 1,
        })}
      >
        <Tooltip title="Open draft sheet" placement="left">
          <IconButton
            size="small"
            onClick={() =>
              activeSnapshotId ? openPanel(activeSnapshotId) : collapsePanel()
            }
            sx={{ color: "primary.main" }}
          >
            <ChevronLeftIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        {activeSnapshotId && (
          <Tooltip title={activeSnapshotId} placement="left">
            <Box
              sx={(t) => ({
                width: 4,
                height: 36,
                borderRadius: 2,
                background: t.palette.accent.main,
                boxShadow: t.palette.accent.glow,
              })}
            />
          </Tooltip>
        )}
      </Box>
    );
  }

  // panelMode === "open"
  return (
    <>
      <Box
        sx={(t) => ({
          // Mobile fold-down: under the sm breakpoint the sheet
          // overlays the full viewport instead of pushing content
          // into a 0-width column. Drag handle + width-resize are
          // skipped on mobile (touch can't usefully drag a 4px
          // handle); the sheet uses position:fixed so it floats over
          // the rest of the layout the way a Drawer does.
          width: { xs: "100vw", sm: panelWidth },
          flex: { xs: undefined, sm: "0 0 auto" },
          position: { xs: "fixed", sm: "relative" },
          inset: { xs: 0, sm: "auto" },
          zIndex: { xs: t.zIndex.modal - 1, sm: "auto" },
          borderLeft: { xs: 0, sm: `1px solid ${t.palette.border.default}` },
          bgcolor: t.palette.surface.default,
          display: "flex",
          flexDirection: "column",
          minHeight: 0,
        })}
      >
        {/* Drag-handle is desktop-only — touch users can't usefully
            grab a 4px target. */}
        <Box sx={{ display: { xs: "none", sm: "block" } }}>
          <DraftResizeHandle />
        </Box>

        {/* Header */}
        <Stack
          direction="row"
          alignItems="center"
          spacing={0.5}
          sx={(t) => ({
            px: 1.75,
            py: 1,
            borderBottom: `1px solid ${t.palette.border.default}`,
          })}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="overline"
              sx={{
                color: "text.disabled",
                fontSize: 10,
                lineHeight: 1,
                letterSpacing: "0.12em",
              }}
            >
              DRAFT
            </Typography>
            {activeSnapshotId ? (
              <Typography
                sx={(t) => ({
                  fontFamily: t.fonts.mono,
                  fontSize: 12,
                  color: "text.primary",
                  letterSpacing: "0.02em",
                })}
              >
                {activeSnapshotId.slice(0, 14)}…
              </Typography>
            ) : (
              <Typography variant="body2" color="text.disabled">
                No active iteration
              </Typography>
            )}
          </Box>
          <Tooltip title="Collapse to rail">
            <IconButton size="small" onClick={collapsePanel}>
              <ChevronRightIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Hide draft sheet">
            <IconButton size="small" onClick={closePanel}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>

        {/* Tabs */}
        <Tabs
          value={panelTab}
          onChange={(_, v) => setPanelTab(v)}
          variant="scrollable"
          sx={{ minHeight: 36 }}
        >
          <Tab value="findings" label="Findings" />
          <Tab value="lineage" label="Lineage" />
          <Tab value="inspect" label="Inspect" />
          <Tab value="bom" label="BOM" />
        </Tabs>

        {/* Body */}
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
          }}
        >
          {!activeSnapshotId && (
            <Box sx={{ p: 2 }}>
              <BlueprintEmptyState
                motif={PenMotif}
                eyebrow="WORKING BUFFER"
                headline="No active draft"
                body="Drafts appear here automatically when the agent edits a circuit. Ask in chat to seed your first iteration."
              />
            </Box>
          )}

          {activeSnapshotId && isLoading && (
            <Stack
              sx={{ flex: 1, alignItems: "center", justifyContent: "center" }}
            >
              <CircularProgress size={20} />
            </Stack>
          )}

          {activeSnapshotId && snapshot && panelTab === "findings" && (
            <DrcFindingsDrawer snapshotId={activeSnapshotId} />
          )}

          {activeSnapshotId && snapshot && panelTab === "lineage" && (
            <OpTimelineStrip
              snapshotId={activeSnapshotId}
              onSelect={setActiveSnapshot}
            />
          )}

          {activeSnapshotId && snapshot && panelTab === "inspect" && (
            <Box sx={{ p: 2 }}>
              <Stack spacing={1.25}>
                <Stack direction="row" spacing={1} flexWrap="wrap">
                  <Chip
                    size="small"
                    label={`${snapshot.ir.components.length} components`}
                    variant="outlined"
                  />
                  <Chip
                    size="small"
                    label={`${snapshot.ir.nets.length} nets`}
                    variant="outlined"
                  />
                  {snapshot.parent_id && (
                    <Tooltip title="Parent snapshot id">
                      <Chip
                        size="small"
                        label={`from ${snapshot.parent_id.slice(0, 8)}…`}
                        variant="outlined"
                        sx={{ fontFamily: "monospace" }}
                      />
                    </Tooltip>
                  )}
                </Stack>
                <Typography variant="caption" color="text.secondary">
                  Source format: {snapshot.ir.source_format}
                </Typography>
                <Typography variant="caption" color="text.disabled">
                  Open-in-canvas gesture lands once the Phase 8 full-screen IR
                  canvas ships.
                </Typography>
              </Stack>
            </Box>
          )}

          {activeSnapshotId && snapshot && panelTab === "bom" && (
            <Box sx={{ p: 2 }}>
              <Stack spacing={1.5}>
                <Button
                  component="a"
                  href={exportSnapshotBomCsvUrl(activeSnapshotId)}
                  target="_blank"
                  rel="noopener"
                  variant="outlined"
                  startIcon={<DownloadIcon />}
                >
                  Download BOM CSV
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<BomIcon />}
                  onClick={() => setBomOpen(true)}
                >
                  Merge BOM into snapshot
                </Button>
              </Stack>
            </Box>
          )}
        </Box>

        {/* Footer — commit gesture stays visible across all tabs */}
        {activeSnapshotId && snapshot && (
          <>
            <Divider sx={{ borderColor: "border.default" }} />
            <Box sx={{ p: 1.5 }}>
              {projectId ? (
                <Button
                  fullWidth
                  variant="contained"
                  startIcon={<CommitIcon />}
                  onClick={() => setCommitOpen(true)}
                >
                  Commit as new version
                </Button>
              ) : (
                <>
                  <Button
                    fullWidth
                    variant="contained"
                    color="secondary"
                    onClick={() => setPromoteOpen(true)}
                  >
                    Promote scratch to project
                  </Button>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ mt: 1, display: "block" }}
                  >
                    Pin this scratch's snapshot chain and create v1 of a new
                    project.
                  </Typography>
                </>
              )}
            </Box>
          </>
        )}
      </Box>

      <CommitDraftDialog
        open={commitOpen}
        snapshotId={activeSnapshotId}
        projectId={projectId}
        parentVersionId={parentVersionId}
        onClose={() => setCommitOpen(false)}
      />
      <BomMergeDialog
        open={bomOpen}
        snapshotId={activeSnapshotId}
        onClose={() => setBomOpen(false)}
        onMerged={(report) => setActiveSnapshot(report.snapshot_id)}
      />
      <PromoteScratchDialog
        open={promoteOpen}
        snapshotId={activeSnapshotId}
        onClose={() => setPromoteOpen(false)}
      />
    </>
  );
}
